import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusDisplay } from "../components/ui/StatusDisplay";
import { Card } from "../components/ui/Card";

// Define interfaces for component state
interface SessionStats {
  passedCount: number;
  failedCount: number;
  totalCount: number;
}

// Define scan status type
type ScanStatus = boolean | null;

export const OperatorView: React.FC = () => {
  const navigate = useNavigate();
  const { operatorName, addScanRecord } = useAppContext();

  const [scannedCode, setScannedCode] = useState<string>("");
  const [scanResult, setScanResult] = useState<ScanStatus>(null);
  const [showManualInput, setShowManualInput] = useState<boolean>(false);
  const [manualCode, setManualCode] = useState<string>("");

  // Keep the session stats state (for future reference if needed)
  const [sessionStats, setSessionStats] = useState<SessionStats>({
    passedCount: 0,
    failedCount: 0,
    totalCount: 0,
  });

  // Refs for barcode scanning with proper types
  const barcodeBuffer = useRef<string>("");
  const barcodeTimeout = useRef<number | null>(null);

  // Handle barcode scanner input
  useEffect(() => {
    // Barcode scanner typically sends input very fast followed by an Enter key
    const handleKeyDown = (e: KeyboardEvent): void => {
      // If the manual input modal is open, don't capture keyboard input
      if (showManualInput) return;

      // If Enter key is pressed, process the accumulated buffer as a barcode
      if (e.key === "Enter" && barcodeBuffer.current) {
        e.preventDefault();
        const code = barcodeBuffer.current;
        barcodeBuffer.current = ""; // Clear buffer
        handleCodeScan(code);
        return;
      }

      // Add character to buffer
      if (e.key.length === 1) {
        barcodeBuffer.current += e.key;

        // Reset the timeout each time a new character is added
        if (barcodeTimeout.current) {
          window.clearTimeout(barcodeTimeout.current);
        }

        // Set a timeout to clear the buffer if no more input is received
        // This distinguishes between manual typing and barcode scanner
        barcodeTimeout.current = window.setTimeout(() => {
          // If the buffer still has content after timeout and no Enter was pressed,
          // it might still be a barcode (some scanners don't send Enter)
          if (barcodeBuffer.current.length >= 4) {
            // Assume codes are at least 4 chars
            const code = barcodeBuffer.current;
            barcodeBuffer.current = "";
            handleCodeScan(code);
          } else {
            barcodeBuffer.current = ""; // Clear buffer if it's likely not a barcode
          }
        }, 100); // Short timeout as barcode scanners send characters rapidly
      }
    };

    // Add the event listener
    document.addEventListener("keydown", handleKeyDown);

    // Clean up
    return (): void => {
      document.removeEventListener("keydown", handleKeyDown);
      if (barcodeTimeout.current) {
        window.clearTimeout(barcodeTimeout.current);
      }
    };
  }, [showManualInput]); // Re-attach listener if showManualInput changes

  // Process scanned code - no whitelist validation
  const handleCodeScan = (
    code: string,
    isManualOverride: boolean = false
  ): void => {
    setScannedCode(code);
    // All scans are now considered valid since there's no whitelist
    const isValid = true;
    setScanResult(isValid);

    // Update session statistics
    setSessionStats(
      (prev: SessionStats): SessionStats => ({
        passedCount: prev.passedCount + 1, // All scans pass now
        failedCount: prev.failedCount,
        totalCount: prev.totalCount + 1,
      })
    );

    // Add to scan history
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    addScanRecord({
      code,
      passed: isValid,
      isManualOverride,
    });
  };

  // Handle manual code submission
  const handleManualSubmit = (): void => {
    if (manualCode.trim()) {
      handleCodeScan(manualCode.trim(), true); // Set manual override flag to true
      setManualCode("");
      setShowManualInput(false);
    }
  };

  // Handle exit button click
  const handleExit = (): void => {
    navigate("/");
  };

  return (
    <div className="h-screen w-screen overflow-hidden p-4 bg-gray-50">
      <PageHeader
        title={
          <>
            Operator: <span className="text-gray-800">{operatorName}</span>
          </>
        }
        onExit={handleExit}
      />

      <div className="flex flex-col items-center justify-center gap-6">
        {/* Simplified Session Stats Display - Only Passed Count */}
        <div className="flex gap-2 items-center w-full max-w-[80vw]">
          <h3 className="text-lg font-medium">Passed Barcode(s):</h3>
          <div className="flex items-center gap-2">
            <div className="text-3xl font-bold">{sessionStats.passedCount}</div>
          </div>
        </div>
        {/* PASS/FAIL Display */}
        <StatusDisplay
          status={
            scanResult === null ? "ready" : scanResult ? "success" : "error"
          }
          className="max-w-[80vw] h-90"
        />

        {/* Barcode Display */}
        <Card className="w-full max-w-[80vw]" padding="lg">
          <h3 className="text-xl font-medium mb-3">Barcode:</h3>
          <div className="p-5 bg-gray-100 rounded min-h-[60px] font-mono text-xl">
            {scannedCode || "No code scanned yet"}
          </div>
        </Card>

        {/* Control Buttons */}
        <div className="flex gap-4">
          {/* <Button variant="primary" size="lg" onClick={simulateScan}>
            Simulate Scan
          </Button> */}

          <Button
            variant="secondary"
            size="lg"
            className="bg-transparent border border-gray-300 rounded hover:bg-gray-500"
            onClick={() => setShowManualInput(true)}
          >
            <span className="text-gray-300">Manual</span>
          </Button>
        </div>
      </div>

      {/* Manual Input Modal */}
      <Modal
        isOpen={showManualInput}
        onClose={() => setShowManualInput(false)}
        title="Enter Barcode Manually"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowManualInput(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleManualSubmit}>
              Submit
            </Button>
          </>
        }
      >
        <Input
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          placeholder="Enter barcode"
          autoFocus
        />
      </Modal>
    </div>
  );
};
